import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

const works = [
  {
    title: 'Discography',
    artist: 'Laura Pausini',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mastering',
    image: 'https://www.audioxlmilano.com/assets/laura-pausini-laura-pausini-album-discografia-1000x1000.jpg',
  },
  {
    title: 'The Beatles Songbook',
    artist: 'Mina',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mastering',
    image: 'https://www.audioxlmilano.com/assets/mina-the-beatles-songbook-album-1000x1000.jpg',
  },
  {
    title: 'Melodia Proibita',
    artist: 'Irama',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mastering',
    image: 'https://www.audioxlmilano.com/assets/Irama-Melodia-Proibita-1-1000x1000.jpg',
  },
  {
    title: 'Plaza Deluxe',
    artist: 'Capoplaza',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mastering',
    image: 'https://www.audioxlmilano.com/assets/capo-plaza-plaza-deluxe-1000x1000.jpg',
  },
  {
    title: 'Bye Bye',
    artist: 'Annalisa',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mastering',
    image: 'https://www.audioxlmilano.com/assets/annalisa-bye-bye-album-1000x1000.jpg',
  },
  {
    title: 'Loyalty',
    artist: 'Mosè COV',
    client: 'Warner Music Italy',
    service: 'Production, Mix, Mastering',
    image: 'https://www.audioxlmilano.com/assets/Mose_loyalty-scaled-1000x1000.jpg',
  },
  {
    title: 'Baby Mama',
    artist: 'Don Pero',
    client: 'Warner Music Italy',
    service: 'Recording, Mix, Mastering',
    image: 'https://www.audioxlmilano.com/assets/don-pero-baby-mama-1000x1000.jpg',
  },
  {
    title: 'Come Mai Prima - SPOT',
    artist: null,
    client: 'Coca Cola',
    service: 'Recording, Mix, Translation',
    image: 'https://www.audioxlmilano.com/assets/Cocacola_come-mai-1000x1000.jpg',
    videoUrl: 'https://www.youtube.com/embed/DQCojy1lNgs',
  },
  {
    title: 'Discography',
    artist: '883',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mastering',
    image: 'https://www.audioxlmilano.com/assets/883-1-in-album_2-1000x1000.jpg',
  },
  {
    title: 'Fuego Del Calor',
    artist: 'Scott Storch feat. Ozuna & Tyga',
    client: 'Capo Plaza / Scott Storch / Tyga / Ozuna',
    service: 'Mastering',
    image: 'https://www.audioxlmilano.com/assets/Scotch-1000x1000.jpg',
  },
  {
    title: 'Un Passo Alla Volta',
    artist: 'Ghali',
    client: 'Comune di Milano',
    service: 'Production & Sound Design',
    image: 'https://www.audioxlmilano.com/assets/Comune_unpasso-1000x1000.jpg',
    videoUrl: 'https://www.youtube.com/embed/L0HPAsD6DsQ',
  },
  {
    title: 'Il Giorno In Cui Ho Smesso Di Pensare',
    artist: 'Irama',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mastering',
    image: 'https://www.audioxlmilano.com/assets/0x1900-000000-80-0-0.jpg',
  },
  {
    title: 'Bershka',
    artist: 'Rkomi',
    client: 'Bershka',
    service: 'Event Production, Streaming Video',
    image: 'https://www.audioxlmilano.com/assets/AXL_proj-9-1-1000x1000.png',
  },
  {
    title: '14/16 Studios',
    artist: null,
    client: 'Warner Music Italy',
    service: 'Studio Manager, Recording, Mix & Mastering',
    image: '/assets/wmi.png',
    imageStyle: 'contain',
  },
  {
    title: 'Lauro',
    artist: 'Achille Lauro',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mastering',
    image: 'https://www.audioxlmilano.com/assets/AXL_proj-2-1-1000x1000.png',
  },
  {
    title: 'Wallah',
    artist: 'Ghali',
    client: 'Warner Music Italy',
    service: 'Dolby Atmos Mix & Mastering',
    image: 'https://www.audioxlmilano.com/assets/wallah-1000x1000.jpg',
  },
];

const workLabel = (work) =>
  `${work.title}${work.artist ? `, ${work.artist}` : ''}: ${work.service}${
    work.videoUrl ? ', watch video' : ''
  }`;

const WorkDialog = ({ work, onClose }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  // Focus the close button, keep Tab inside the dialog, close with Escape,
  // and give focus back to the tile that opened it.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeRef.current?.focus();
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusables = dialogRef.current.querySelectorAll('button, [href], iframe');
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  const panelMotion = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.95, opacity: 0 },
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xl ${
        work.videoUrl ? 'bg-black/95' : 'bg-black/90'
      }`}
      onClick={onClose}
      data-testid={work.videoUrl ? 'video-modal' : 'work-modal'}
    >
      {work.videoUrl ? (
        <motion.div
          {...panelMotion}
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="work-dialog-title"
          className="relative w-full max-w-5xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute -top-14 right-0 z-10 p-2 text-white/70 hover:text-white transition-colors"
            data-testid="video-modal-close"
          >
            <X size={32} aria-hidden="true" />
          </button>

          <div className="aspect-video">
            <iframe
              src={`${work.videoUrl}?autoplay=1&rel=0`}
              title={work.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          <div className="mt-4 text-center">
            <h3 id="work-dialog-title" className="font-heading font-bold text-xl text-white">
              {work.title}
            </h3>
            {work.artist && <p className="font-body text-white/70 mt-1">{work.artist}</p>}
          </div>
        </motion.div>
      ) : (
        <motion.div
          {...panelMotion}
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="work-dialog-title"
          className="relative max-w-4xl w-full bg-[#121212] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 z-10 p-3 bg-black/50 hover:bg-white hover:text-black transition-all duration-300"
            data-testid="modal-close"
          >
            <X size={20} aria-hidden="true" />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="aspect-square">
              <img
                src={work.image}
                alt={`${work.title} cover`}
                className={`w-full h-full ${
                  work.imageStyle === 'contain' ? 'object-contain p-8 bg-white' : 'object-cover'
                }`}
              />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <span className="font-body text-xs tracking-[0.2em] text-white/60 uppercase">
                {work.service}
              </span>
              <h3 id="work-dialog-title" className="font-heading font-bold text-2xl md:text-3xl mt-4">
                {work.title}
              </h3>
              {work.artist && (
                <p className="font-body text-lg text-white/70 mt-2">{work.artist}</p>
              )}
              <div className="mt-8 pt-8 border-t border-white/10">
                <span className="font-body text-xs tracking-[0.2em] text-white/60 uppercase">
                  Client
                </span>
                <p className="font-body text-base text-white/70 mt-2">{work.client}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const Works = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [selectedWork, setSelectedWork] = useState(null);

  const closeModal = useCallback(() => setSelectedWork(null), []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <>
      <section
        id="works"
        data-testid="works-section"
        ref={ref}
        className="relative py-32 md:py-48 px-6 md:px-12 lg:px-24 bg-[#0a0a0a]"
      >
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mb-16 md:mb-24"
          >
            <span className="font-heading font-bold text-sm tracking-[0.3em] text-white/40 uppercase">
              Portfolio
            </span>
            <h2
              className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl leading-tight mt-6"
              data-testid="works-heading"
            >
              SELECTED
              <br />
              WORKS
            </h2>
          </motion.div>

          {/* Works Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6 md:gap-6"
          >
            {works.map((work, index) => (
              <motion.div key={index} variants={itemVariants}>
                <button
                  type="button"
                  onClick={() => setSelectedWork(work)}
                  aria-label={workLabel(work)}
                  aria-haspopup="dialog"
                  className="group relative block w-full aspect-square overflow-hidden bg-white/[0.02] text-left"
                  data-testid={`work-${index}`}
                >
                  <img
                    src={work.image}
                    alt=""
                    className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${
                      work.imageStyle === 'contain' ? 'object-contain p-8 bg-white' : 'object-cover'
                    }`}
                    loading="lazy"
                  />
                  {/* Hover caption, only on devices with a pointer */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-500 hidden [@media(hover:hover)]:flex flex-col justify-end p-4"
                  >
                    <h3 className="font-heading font-bold text-sm md:text-base leading-tight">
                      {work.title}
                    </h3>
                    {work.artist && (
                      <p className="font-body text-xs text-white/80 mt-1">{work.artist}</p>
                    )}
                    <p className="font-body text-xs text-white/70 mt-2 uppercase tracking-wider">
                      {work.service}
                    </p>
                    <p className="font-body text-xs text-white/60 mt-1">{work.client}</p>
                    {work.videoUrl && (
                      <div className="mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                        <span className="text-xs text-white/80">Watch Video</span>
                      </div>
                    )}
                  </div>
                </button>

                {/* Always-visible caption on touch devices */}
                <div aria-hidden="true" className="mt-3 [@media(hover:hover)]:hidden">
                  <p className="font-heading font-bold text-sm leading-tight">{work.title}</p>
                  {work.artist && (
                    <p className="font-body text-xs text-white/70 mt-1">{work.artist}</p>
                  )}
                  <p className="font-body text-xs text-white/60 mt-1">
                    {work.service}
                    {work.videoUrl && ' · Video'}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {selectedWork && <WorkDialog key="work-dialog" work={selectedWork} onClose={closeModal} />}
      </AnimatePresence>
    </>
  );
};

export default Works;
